// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Notification, NotificationSearchFilters, NotificationCode } from '../../types/notifications';
import { UserRole } from '../../types/auth';
import { consoleService } from '../../services/consoleService';
import { authService } from '../../services/authService';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../ConfirmDialog';
import './Notifications.scss';

// SVG Icons
const RedTriangleIcon = () => (
  <img src="/static/svg/red-triangle.svg" alt="" width="16" height="16" className="mr-2" />
);

const PageFirstIcon = () => (
  <img src="/static/svg/page-first.svg" alt="First page" width="20" height="20" />
);

const PageNextIcon = () => (
  <img src="/static/svg/page-next.svg" alt="Next page" width="20" height="20" />
);

const LoadingSpinner = () => (
  <div className="spinner-border spinner-border-sm" role="status">
    <span className="visually-hidden">Loading...</span>
  </div>
);

const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { dialogState, openConfirmDialog, closeConfirmDialog, handleConfirm } = useConfirmDialog();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [nextCursor, setNextCursor] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchFilters, setSearchFilters] = useState<NotificationSearchFilters>({
    user_id: '',
    username: '',
    subject: '',
    content: '',
    code: undefined,
    sender_id: '',
    persistent: undefined,
  });

  const sessionRole = authService.getSessionRole();

  // Initialize filters from URL params
  useEffect(() => {
    const userId = searchParams.get('user_id') || '';
    const username = searchParams.get('username') || '';
    const subject = searchParams.get('subject') || '';
    const content = searchParams.get('content') || '';
    const senderId = searchParams.get('sender_id') || '';
    const code = searchParams.get('code');
    const persistent = searchParams.get('persistent');
    const cursor = searchParams.get('cursor') || '';

    setSearchFilters({
      user_id: userId,
      username,
      subject,
      content,
      sender_id: senderId,
      code: code ? parseInt(code) : undefined,
      persistent: persistent ? persistent === 'true' : undefined,
    });
    setNextCursor(cursor);

    if (cursor || userId || username || subject || content || senderId || code || persistent) {
      loadNotifications(cursor, {
        user_id: userId,
        username,
        subject,
        content,
        sender_id: senderId,
        code: code ? parseInt(code) : undefined,
        persistent: persistent ? persistent === 'true' : undefined,
      });
    } else {
      loadNotifications();
    }
  }, []);

  const loadNotifications = useCallback(async (
    cursor?: string,
    filters?: NotificationSearchFilters
  ) => {
    setLoading(true);
    setError('');

    try {
      const result = await consoleService.listNotifications(
        '',
        filters || searchFilters,
        cursor
      );

      setNotifications(result.notifications || []);
      setNextCursor(result.cursor || '');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [searchFilters]);

  const handleSearch = useCallback((direction: number = 0) => {
    let cursor = '';
    switch (direction) {
      case 0:
        cursor = '';
        break;
      case 1:
        cursor = nextCursor;
        break;
    }

    // Update URL params
    const params = new URLSearchParams();
    if (searchFilters.user_id) params.set('user_id', searchFilters.user_id);
    if (searchFilters.username) params.set('username', searchFilters.username);
    if (searchFilters.subject) params.set('subject', searchFilters.subject);
    if (searchFilters.content) params.set('content', searchFilters.content);
    if (searchFilters.sender_id) params.set('sender_id', searchFilters.sender_id);
    if (searchFilters.code !== undefined) params.set('code', searchFilters.code.toString());
    if (searchFilters.persistent !== undefined) params.set('persistent', searchFilters.persistent.toString());
    if (cursor) params.set('cursor', cursor);
    
    setSearchParams(params);
    loadNotifications(cursor, searchFilters);
  }, [searchFilters, nextCursor, setSearchParams, loadNotifications]);

  const handleFilterChange = (field: keyof NotificationSearchFilters, value: string | number | boolean | undefined) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(0);
  };

  const handleDeleteNotification = (notification: Notification, index: number) => {
    openConfirmDialog(
      'Delete Notification',
      `Are you sure you want to delete this notification for "${notification.username || notification.user_id}"? This action cannot be undone.`,
      async () => {
        try {
          await consoleService.deleteNotification('', {
            notification_id: notification.id,
            user_id: notification.user_id
          });
          setNotifications(prev => prev.filter((_, i) => i !== index));
          setError('');
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to delete notification');
        }
      }
    );
  };

  const handleViewNotification = (notification: Notification) => {
    navigate(`/notifications/${notification.id}?user_id=${notification.user_id}`);
  };

  const deleteAllowed = () => {
    return sessionRole === UserRole.USER_ROLE_ADMIN;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  const getNotificationTypeText = (code: number) => {
    switch (code) {
      case NotificationCode.FRIEND_REQUEST: return 'Friend Request';
      case NotificationCode.FRIEND_ACCEPT: return 'Friend Accept';
      case NotificationCode.FRIEND_REJECT: return 'Friend Reject';
      case NotificationCode.FRIEND_REMOVE: return 'Friend Remove';
      case NotificationCode.GROUP_ADD: return 'Group Add';
      case NotificationCode.GROUP_JOIN_REQUEST: return 'Group Join Request';
      case NotificationCode.GROUP_JOIN_ACCEPT: return 'Group Join Accept';
      case NotificationCode.GROUP_JOIN_REJECT: return 'Group Join Reject';
      case NotificationCode.GROUP_KICK: return 'Group Kick';
      case NotificationCode.GROUP_PROMOTE: return 'Group Promote';
      case NotificationCode.GROUP_DEMOTE: return 'Group Demote';
      case NotificationCode.GROUP_BAN: return 'Group Ban';
      case NotificationCode.CHAT_MESSAGE: return 'Chat Message';
      case NotificationCode.CHAT_CHANNEL_UPDATE: return 'Chat Channel Update';
      case NotificationCode.LEADERBOARD_RECORD: return 'Leaderboard Record';
      case NotificationCode.TOURNAMENT_START: return 'Tournament Start';
      case NotificationCode.TOURNAMENT_END: return 'Tournament End';
      case NotificationCode.CUSTOM: return 'Custom';
      default: return `Code ${code}`;
    }
  };

  const getNotificationTypeBadgeClass = (code: number) => {
    switch (code) {
      case NotificationCode.FRIEND_REQUEST:
      case NotificationCode.FRIEND_ACCEPT:
      case NotificationCode.FRIEND_REJECT:
      case NotificationCode.FRIEND_REMOVE:
        return 'friend';
      case NotificationCode.GROUP_ADD:
      case NotificationCode.GROUP_JOIN_REQUEST:
      case NotificationCode.GROUP_JOIN_ACCEPT:
      case NotificationCode.GROUP_JOIN_REJECT:
      case NotificationCode.GROUP_KICK:
      case NotificationCode.GROUP_PROMOTE:
      case NotificationCode.GROUP_DEMOTE:
      case NotificationCode.GROUP_BAN:
        return 'group';
      case NotificationCode.CHAT_MESSAGE:
      case NotificationCode.CHAT_CHANNEL_UPDATE:
        return 'chat';
      case NotificationCode.LEADERBOARD_RECORD:
        return 'leaderboard';
      case NotificationCode.TOURNAMENT_START:
      case NotificationCode.TOURNAMENT_END:
        return 'tournament';
      case NotificationCode.CUSTOM:
        return 'custom';
      default:
        return 'unknown';
    }
  };

  const truncateContent = (content: string, maxLength: number = 100) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  return (
    <div className="notifications-container">
      <div className="notifications-header">
        <h2 className="notifications-title">Notifications</h2>
        <h6 className="notifications-subtitle">{notifications.length} notifications found.</h6>
      </div>

      <div className="notifications-controls">
        <div className="search-section">
          <form onSubmit={handleSubmit} className="search-form">
            <div className="search-input-group">
              <input
                type="text"
                className="search-input"
                value={searchFilters.user_id || ''}
                onChange={(e) => handleFilterChange('user_id', e.target.value)}
                placeholder="Filter by user ID"
              />
              <input
                type="text"
                className="search-input"
                value={searchFilters.username || ''}
                onChange={(e) => handleFilterChange('username', e.target.value)}
                placeholder="Filter by username"
              />
              <input
                type="text"
                className="search-input"
                value={searchFilters.subject || ''}
                onChange={(e) => handleFilterChange('subject', e.target.value)}
                placeholder="Filter by subject"
              />
              <input
                type="text"
                className="search-input"
                value={searchFilters.content || ''}
                onChange={(e) => handleFilterChange('content', e.target.value)}
                placeholder="Filter by content"
              />
              <select
                className="code-select"
                value={searchFilters.code || ''}
                onChange={(e) => handleFilterChange('code', e.target.value ? parseInt(e.target.value) : undefined)}
              >
                <option value="">All Types</option>
                <option value={NotificationCode.FRIEND_REQUEST}>Friend Request</option>
                <option value={NotificationCode.FRIEND_ACCEPT}>Friend Accept</option>
                <option value={NotificationCode.GROUP_ADD}>Group Add</option>
                <option value={NotificationCode.CHAT_MESSAGE}>Chat Message</option>
                <option value={NotificationCode.LEADERBOARD_RECORD}>Leaderboard Record</option>
                <option value={NotificationCode.TOURNAMENT_START}>Tournament Start</option>
                <option value={NotificationCode.CUSTOM}>Custom</option>
              </select>
              <select
                className="persistent-select"
                value={searchFilters.persistent === undefined ? '' : searchFilters.persistent.toString()}
                onChange={(e) => handleFilterChange('persistent', e.target.value === '' ? undefined : e.target.value === 'true')}
              >
                <option value="">All Notifications</option>
                <option value="true">Persistent</option>
                <option value="false">Non-Persistent</option>
              </select>
              <div className="search-buttons">
                <button
                  type="submit"
                  className="btn btn-primary search-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-1" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Searching...
                    </>
                  ) : (
                    'Search'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        <div className="pagination-section">
          <div className="pagination-buttons">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => handleSearch(0)}
              disabled={notifications.length === 0 || loading}
              title="First page"
            >
              <PageFirstIcon />
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => handleSearch(1)}
              disabled={!nextCursor || loading}
              title="Next page"
            >
              <PageNextIcon />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <RedTriangleIcon />
          <strong>Error when querying notifications:</strong> {error}
        </div>
      )}

      {loading && (
        <div className="alert alert-info">
          <LoadingSpinner />
          <strong className="ms-2">Loading notifications...</strong>
        </div>
      )}

      <div className="notifications-table-container">
        <table className="notifications-table">
          <thead>
            <tr>
              <th style={{ width: '150px' }}>Type</th>
              <th style={{ width: '200px' }}>Recipient</th>
              <th style={{ width: '200px' }}>Subject</th>
              <th>Content</th>
              <th style={{ width: '80px' }}>Persistent</th>
              <th style={{ width: '180px' }}>Created</th>
              {deleteAllowed() && <th style={{ width: '90px' }}>Remove</th>}
            </tr>
          </thead>
          <tbody>
            {notifications.length === 0 && !loading && (
              <tr>
                <td colSpan={deleteAllowed() ? 7 : 6} className="no-notifications">
                  No notifications found - change the filter criteria.
                </td>
              </tr>
            )}
            {notifications.map((notification, index) => (
              <tr key={notification.id} className="notification-row">
                <td onClick={() => handleViewNotification(notification)} className="clickable">
                  <span className={`type-badge ${getNotificationTypeBadgeClass(notification.code)}`}>
                    {getNotificationTypeText(notification.code)}
                  </span>
                </td>
                <td onClick={() => handleViewNotification(notification)} className="clickable">
                  <div className="recipient-info">
                    <div className="username">{notification.username || '-'}</div>
                    <code className="user-id">{notification.user_id}</code>
                  </div>
                </td>
                <td onClick={() => handleViewNotification(notification)} className="clickable">
                  <div className="notification-subject">
                    {notification.subject}
                  </div>
                </td>
                <td onClick={() => handleViewNotification(notification)} className="clickable">
                  <div className="notification-content">
                    {truncateContent(notification.content)}
                  </div>
                </td>
                <td onClick={() => handleViewNotification(notification)} className="clickable">
                  <span className={`persistent-badge ${notification.persistent ? 'persistent' : 'temporary'}`}>
                    {notification.persistent ? 'Yes' : 'No'}
                  </span>
                </td>
                <td onClick={() => handleViewNotification(notification)} className="clickable">
                  {formatDate(notification.create_time)}
                </td>
                {deleteAllowed() && (
                  <td className="delete-cell">
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteNotification(notification, index)}
                    >
                      Delete
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        onConfirm={handleConfirm}
        onCancel={closeConfirmDialog}
      />
    </div>
  );
};

export default Notifications;
